/**
 * Cross-tab notification after a successful IntentML company import (ISSUE-220).
 *
 * Strategy: **BroadcastChannel** primary; **localStorage** write as fallback so other tabs
 * on the same origin receive a `storage` event (BroadcastChannel is same-origin but
 * storage works across tabs that share the profile).
 */

export const INTENTML_SNAPSHOT_CHANNEL = 'krysalis-intentml-snapshot';

export const INTENTML_SNAPSHOT_STORAGE_KEY = 'krysalis_intentml_snapshot_tick';

export type IntentmlSnapshotMessage = {
  type: 'intentml-import-complete';
  companyId: string;
  at: string;
  /** Count of pt-icp-definition artifact_items after import (optional; from platform API). */
  icp_definition_item_count?: number;
};

export type BroadcastIntentmlImportOptions = {
  icpDefinitionItemCount?: number;
};

/** Notify other tabs/windows that company model data may have changed. */
export function broadcastIntentmlImportComplete(
  companyId: string,
  options?: BroadcastIntentmlImportOptions
): void {
  const payload: IntentmlSnapshotMessage = {
    type: 'intentml-import-complete',
    companyId,
    at: new Date().toISOString(),
    ...(typeof options?.icpDefinitionItemCount === 'number'
      ? { icp_definition_item_count: options.icpDefinitionItemCount }
      : {}),
  };
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(INTENTML_SNAPSHOT_CHANNEL);
      ch.postMessage(payload);
      ch.close();
    }
  } catch {
    /* ignore */
  }
  try {
    localStorage.setItem(INTENTML_SNAPSHOT_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function parseIntentmlSnapshotTick(raw: string | null): IntentmlSnapshotMessage | null {
  if (!raw) return null;
  try {
    const o = JSON.parse(raw) as Record<string, unknown>;
    if (o?.type !== 'intentml-import-complete' || typeof o.companyId !== 'string') {
      return null;
    }
    const out: IntentmlSnapshotMessage = {
      type: 'intentml-import-complete',
      companyId: o.companyId,
      at: typeof o.at === 'string' ? o.at : new Date().toISOString(),
    };
    if (
      'icp_definition_item_count' in o &&
      typeof o.icp_definition_item_count === 'number' &&
      Number.isFinite(o.icp_definition_item_count)
    ) {
      out.icp_definition_item_count = o.icp_definition_item_count;
    }
    return out;
  } catch {
    return null;
  }
}
